import React, { useState, useEffect } from "react";
import { Route, Navigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import { landingFor, userHasRole } from './helpers';
import { buildApiUrl, apiFetch } from '../../utils/apiClient';
import lazyWithRetry from '../../utils/lazyWithRetry';

/**
 * Redirect legacy ``/client-portal/register`` URLs (sent in older invite
 * emails) to the canonical ``/portal/register`` route, preserving query
 * params so the invite token carries through.
 */
function PortalRegistrationRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={`/portal/register?${params.toString()}`} replace />;
}

export function ClientPortalRoute({ user }) {
  const location = useLocation();

  if (user === null) return <div>Checking login...</div>;

  const isDemo = !!user?.demo_mode;
  const isDemoClient =
    isDemo && ['client', 'parent_of_client', 'part_of_couple'].includes(user?.character_type);
  const isClient = userHasRole(user, 'client') || isDemoClient;

  if (isClient) return <ClientPortalPage />;

  return (
    <Navigate
      to="/login"
      replace
      state={{
        from: {
          pathname: '/client',
          search: location.search,
          hash: location.hash,
        },
      }}
    />
  );
}

/**
 * Validates that the URL slug matches the authenticated user's org
 * before rendering the client portal. Redirects to /client if the
 * slug belongs to a different organization.
 */
function OrgScopedPortalGuard({ user }: { user: Record<string, unknown> }) {
  const { slug } = useParams<{ slug: string }>();
  const [validated, setValidated] = useState<boolean | null>(null);

  useEffect(() => {
    if (!slug || !user?.organization_id) {
      setValidated(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = buildApiUrl(`/api/orgs/lookup/${encodeURIComponent(slug)}`);
        const res = await apiFetch(url);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setValidated(data.org_id === user.organization_id);
        } else if (!cancelled) {
          setValidated(false);
        }
      } catch {
        if (!cancelled) setValidated(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, user?.organization_id]);

  if (validated === null) return <div>Checking login...</div>;
  if (!validated) return <Navigate to="/client" replace />;
  return <ClientPortalPage />;
}

// Lazy-loaded pages
const LoginPage = lazyWithRetry(() => import('../../pages/LoginPage'));
const DemoLoginPage = lazyWithRetry(() => import('../../pages/DemoLoginPage'));
const TestingPage = lazyWithRetry(() => import('../../pages/TestingPage'));
// RegistrationPage no longer used — /register redirects to /name-my-clinic
const PortalRegistrationPage = lazyWithRetry(() => import('../../pages/PortalRegistrationPage'));
const ForgotPassword = lazyWithRetry(() => import('../../pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('../../pages/ResetPassword'));
const PublicBookingPage = lazyWithRetry(() => import('../../pages/PublicBookingPage'));
const ProfileSetupPage = lazyWithRetry(() => import('../../pages/ProfileSetupPage'));
const AccountInactivePage = lazyWithRetry(() => import('../../pages/AccountInactivePage'));
const InviteMatchPage = lazyWithRetry(() => import('../../pages/InviteMatchPage'));
const ConversationsPage = lazyWithRetry(() => import('../../pages/ConversationsPage'));
const OAuthCallbackRedirector = lazyWithRetry(() => import('../../pages/OAuthCallbackRedirector'));
const ClientPortalPage = lazyWithRetry(() => import('../../features/client-portal/ClientPortalPage'));
const DevConsolePage = lazyWithRetry(() => import('../../pages/DevConsolePage'));
const MFAVerifyPage = lazyWithRetry(() => import('../../pages/MFAVerifyPage'));
const DevLoginPage = lazyWithRetry(() => import('../../features/dev-login/DevLoginPage'));
const SubscriptionSignUpPage = lazyWithRetry(() => import('../../pages/SubscriptionSignUpPage'));
const GatedDemoPage = lazyWithRetry(() => import('../../pages/GatedDemoPage'));
const CharacterSwitcherPage = lazyWithRetry(() => import('../../pages/CharacterSwitcherPage'));
const PublicEventPage = lazyWithRetry(() => import('../../pages/PublicEventPage'));
const ProviderBookingEmbedPage = lazyWithRetry(() => import('../../features/booking/ProviderBookingEmbedPage'));
const OwnerLoginPage = lazyWithRetry(() => import('../../pages/OwnerLoginPage'));
const OrgLoginPage = lazyWithRetry(() => import('../../pages/OrgLoginPage'));
const PortalLoginPage = lazyWithRetry(() => import('../../pages/PortalLoginPage'));

/**
 * Shared Routes - Authentication, Public, and Profile Routes
 * Routes accessible to all users or unauthenticated users
 */
export function SharedRoutes({ user }) {
  return (
    <>
      <Route path="/api/*" element={null} />
      
      {/* Home route */}
      <Route
        path="/"
        element={
          user === null
            ? <div>Checking login...</div>
            : <Navigate to={landingFor(user)} />
        }
      />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <Navigate to={landingFor(user)} />
            : <LoginPage />
        }
      />
      
      <Route
        path="/demo-login"
        element={<DemoLoginPage />}
      />
      
      <Route
        path="/demo"
        element={<Navigate to="/demo-login" />}
      />

      {/* Full-page character picker for users in a claimed org.  Demo-only
          (pre-claim) users are bounced back to /demo-login by the page. */}
      <Route
        path="/characters"
        element={<CharacterSwitcherPage />}
      />
      
      <Route
        path="/testing"
        element={<TestingPage />}
      />
      
      <Route
        path="/dev"
        element={<DevLoginPage />}
      />
      
      <Route
        path="/book"
        element={<PublicBookingPage />}
      />
      
      {/* Support /book/:practiceCode path format */}
      <Route
        path="/book/:practiceCode"
        element={<PublicBookingPage />}
      />

      {/* Direct inquiry URL — opens booking page pre-set to inquiry mode */}
      <Route
        path="/inquiry/:practiceCode"
        element={<PublicBookingPage />}
      />

      {/* Standalone provider directory — embeddable on clinic websites */}
      <Route
        path="/providers/:practiceCode"
        element={<PublicBookingPage />}
      />
      {/* Direct provider link — shareable URL to a specific provider's bio + calendar */}
      <Route
        path="/providers/:practiceCode/:providerId"
        element={<PublicBookingPage />}
      />

      {/* Embeddable "Book with Dr. X" widget — clinics embed on their website bio pages */}
      <Route
        path="/embed/book/:practiceCode/:providerId"
        element={<ProviderBookingEmbedPage />}
      />

      {/* Public Event Pages */}
      <Route
        path="/events/:practiceCode"
        element={<PublicEventPage />}
      />
      <Route
        path="/events/:practiceCode/:eventId"
        element={<PublicEventPage />}
      />
      
      <Route
        path="/register"
        element={<Navigate to="/name-my-clinic" replace />}
      />

      {/* Name My Clinic — canonical paid clinic-creation flow */}
      <Route
        path="/name-my-clinic"
        element={<SubscriptionSignUpPage />}
      />

      {/* Gated demo — low-barrier time-limited demo access */}
      <Route
        path="/gated-demo"
        element={<GatedDemoPage />}
      />

      {/* User-friendly alias for gated demo */}
      <Route
        path="/try-kindredly-free"
        element={<Navigate to="/gated-demo" replace />}
      />

      {/* Old paid-trial route now redirects to canonical Name My Clinic flow */}
      <Route
        path="/start-trial"
        element={<Navigate to="/name-my-clinic" replace />}
      />

      {/* /subscribe is a legacy alias; the canonical UX is /name-my-clinic */}
      <Route
        path="/subscribe"
        element={<Navigate to="/name-my-clinic" replace />}
      />
      
      {/* Client Portal Registration via Invite Token */}
      <Route
        path="/portal/register"
        element={<PortalRegistrationPage />}
      />

      {/* Legacy redirect — invite emails sent before the URL fix used this path */}
      <Route
        path="/client-portal/register"
        element={<PortalRegistrationRedirect />}
      />
      
      <Route
        path="/forgot-password"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <Navigate to={landingFor(user)} />
            : <ForgotPassword />
        }
      />
      
      <Route
        path="/reset-password"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <Navigate to={landingFor(user)} />
            : <ResetPassword />
        }
      />

      {/* MFA Verification Page */}
      <Route
        path="/mfa-verify"
        element={<MFAVerifyPage />}
      />

      {/* Legacy profile page — canonical editor lives at /my-settings?tab=profile */}
      <Route
        path="/profile"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <Navigate to="/my-settings?tab=profile" replace />
            : <Navigate to="/login" />
        }
      />

      <Route
        path="/profile-setup"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <ProfileSetupPage />
            : <Navigate to="/login" />
        }
      />

      <Route
        path="/account-inactive"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <AccountInactivePage />
            : <Navigate to="/login" />
        }
      />

      <Route
        path="/invite-match"
        element={<InviteMatchPage />}
      />

      {/* Conversations Page */}
      <Route
        path="/conversations"
        element={
          user === null
            ? <div>Checking login...</div>
            : user
            ? <ConversationsPage />
            : <Navigate to="/login" />
        }
      />

      {/* Client Portal alternative route */}
      <Route
        path="/client-portal"
        element={<ClientPortalRoute user={user} />}
      />

      {/* Dev Console - Only for dev role */}
      <Route
        path="/dev-console"
        element={
          user === null
            ? <div>Checking login...</div>
            : user && userHasRole(user, 'dev')
            ? <DevConsolePage />
            : user
            ? <Navigate to={landingFor(user)} />
            : <Navigate to="/login" />
        }
      />

      {/* Platform Owner Login */}
      <Route
        path="/owner-login"
        element={<OwnerLoginPage />}
      />

      {/* OAuth Callback */}
      <Route
        path="/oauth-callback"
        element={<OAuthCallbackRedirector />}
      />

      {/*
       * Org-branded client portal (authenticated client users).
       *
       * OrgScopedPortalGuard validates the URL slug matches the
       * authenticated user's organization before rendering the portal.
       */}
      <Route
        path="/:slug/portal"
        element={
          user === null
            ? <div>Checking login...</div>
            : (() => {
                const isDemo = !!user?.demo_mode;
                const isDemoClient = isDemo && ['client','parent_of_client','part_of_couple'].includes(user?.character_type);
                const isClient = userHasRole(user, 'client') || isDemoClient;
                return isClient ? <OrgScopedPortalGuard user={user} /> : <Navigate to="/login" />;
              })()
        }
      />

      {/*
       * Org-branded client portal login.
       *
       * Provides a dedicated client-facing login page branded with the
       * org's logo and name.  On success the user is redirected to the
       * client portal at /:slug/portal.
       */}
      <Route
        path="/:slug/portal/login"
        element={<PortalLoginPage />}
      />

      {/*
       * Org-branded client portal registration via invite token.
       *
       * The :slug is available via useParams() inside PortalRegistrationPage,
       * but the component currently uses the invite token (query param) to
       * resolve the org — the slug is cosmetic only.
       *
       * Future: pass slug context to PortalRegistrationPage and validate
       *         that the invite token's org matches the slug's org.
       */}
      <Route
        path="/:slug/portal/register"
        element={<PortalRegistrationPage />}
      />

      {/*
       * Canonical org-specific staff login URL.  Keep the shorter /:slug
       * route below as a backwards-compatible alias.
       */}
      {user === null ? (
        <Route path="/:slug/login" element={<div>Checking login...</div>} />
      ) : !user ? (
        <Route path="/:slug/login" element={<OrgLoginPage />} />
      ) : null}

      {/*
       * Org-specific login URL (catch-all — must be last).
       *
       * IMPORTANT: We conditionally EXCLUDE this <Route> entirely when
       * the user is logged in.  In React Router v6, route matching is
       * based on path specificity — a dynamic segment (/:slug) always
       * outranks a wildcard (*) regardless of the rendered element.
       * If we rendered this Route for logged-in users (even with
       * element={null}), it would match single-segment paths like
       * /dashboard or /admin before the billing-lockout "*" catch-all
       * in index.tsx, resulting in a blank page instead of the
       * SubscriptionLockoutPage.
       */}
      {user === null ? (
        <Route path="/:slug" element={<div>Checking login...</div>} />
      ) : !user ? (
        <Route path="/:slug" element={<OrgLoginPage />} />
      ) : null}
    </>
  );
}
